/**
 * The live coach.
 *
 * Watches the preview a few times a second and says the one thing worth
 * changing before the shutter is pressed. Its whole job is to steer people
 * toward the photographs the eraser can actually deliver:
 *
 *  - A phone over the face cannot be erased honestly, so it says so while
 *    there is still time to lower the phone, rather than after.
 *  - In hands-free mode the best result is no phone in the picture at all,
 *    so it says when the phone can see itself and how to fix that.
 *
 * It runs in the vision worker with the small detector, so a look costs a few
 * hundred milliseconds of somebody else's thread and never the shutter's. It
 * looks again as soon as it has an answer, which on a phone is a couple of
 * times a second. A capture always looks again with the accurate detector.
 */
import { scan } from '../ml/tasks';
import { FACE_LIMIT, classify, faceBox, type Body, type Box, type Phone } from '../erase/geometry';
import type { Style } from './settings';

export type Tone = 'neutral' | 'good' | 'warn' | 'bad';

export interface CoachState {
  person: boolean;
  phones: Phone[];
  face: Box | null;
  /** The frame size the boxes are in. */
  w: number;
  h: number;
  message: string;
  tone: Tone;
  /** Nothing in the way of a good photograph. */
  ready: boolean;
}

/** Shortest gap between looks; the worker's own pace usually sets it. */
const PERIOD = 250;
/** How long a hand has to stay up before it counts as a signal, not a gesture. */
const RAISE_HOLD = 450;
/** Analysis width for the live frame. */
const LIVE_EDGE = 640;

export interface CoachOptions {
  video: HTMLVideoElement;
  overlay: HTMLCanvasElement;
  onState: (state: CoachState) => void;
  /** Hands-free only: a hand held above the head. */
  onRaise?: () => void;
}

/** Decide what to say. Pure, so the wording can be tested against scenes. */
export function advise(style: Style, bodies: Body[], phones: Phone[], raiseToShoot: boolean): Pick<CoachState, 'message' | 'tone' | 'ready'> {
  if (bodies.length === 0) {
    return { message: style === 'free' ? 'Step into the mirror.' : 'Find yourself in the mirror.', tone: 'neutral', ready: false };
  }
  const overFace = phones.find((p) => p.coversFace > FACE_LIMIT);
  if (style === 'hand') {
    if (overFace) {
      return { message: 'Your phone is hiding your face. Lower it to your chest — that photograph erases cleanly.', tone: 'bad', ready: false };
    }
    if (phones.length > 0) return { message: 'Ready. Hold still and take it.', tone: 'good', ready: true };
    return { message: 'Hold the phone where you normally would.', tone: 'neutral', ready: true };
  }
  const held = phones.find((p) => p.held);
  if (held) return { message: 'Put the phone down and stand it up facing the mirror.', tone: 'warn', ready: false };
  if (phones.length > 0) {
    return {
      message: 'The phone can see itself. Turn it a little away from the mirror — or shoot anyway and it will be erased.',
      tone: 'warn',
      ready: true,
    };
  }
  return {
    message: raiseToShoot ? 'No phone in the picture. Raise a hand to start the timer.' : 'No phone in the picture. Tap the shutter to start the timer.',
    tone: 'good',
    ready: true,
  };
}

export class Coach {
  #opts: CoachOptions;
  #style: Style = 'hand';
  #raiseToShoot = true;
  #timer: number | null = null;
  #busy = false;
  #frame = document.createElement('canvas');
  #raisedSince = 0;
  #armed = true;

  constructor(opts: CoachOptions) {
    this.#opts = opts;
  }

  configure(style: Style, raiseToShoot: boolean): void {
    this.#style = style;
    this.#raiseToShoot = raiseToShoot;
  }

  start(): void {
    if (this.#timer !== null) return;
    this.#timer = window.setInterval(() => void this.#tick(), PERIOD);
  }

  stop(): void {
    if (this.#timer !== null) window.clearInterval(this.#timer);
    this.#timer = null;
    this.#clear();
  }

  /** Re-allow the raise signal, after a countdown has used it. */
  rearm(): void {
    this.#armed = true;
    this.#raisedSince = 0;
  }

  #clear(): void {
    const ctx = this.#opts.overlay.getContext('2d');
    ctx?.clearRect(0, 0, this.#opts.overlay.width, this.#opts.overlay.height);
  }

  async #tick(): Promise<void> {
    const { video } = this.#opts;
    if (this.#busy || document.hidden || !video.videoWidth) return;
    this.#busy = true;
    try {
      // A modest copy: the coach needs the gist, not the pixels.
      const scale = Math.min(1, LIVE_EDGE / Math.max(video.videoWidth, video.videoHeight));
      const w = Math.round(video.videoWidth * scale);
      const h = Math.round(video.videoHeight * scale);
      if (this.#frame.width !== w || this.#frame.height !== h) {
        this.#frame.width = w;
        this.#frame.height = h;
      }
      this.#frame.getContext('2d')!.drawImage(video, 0, 0, w, h);

      const look = await scan(this.#frame, 'coach');
      if (this.#timer === null) return; // stopped while looking
      const phones = classify(look.found, look.bodies);
      const main = [...look.bodies].sort((a, b) => b.shoulderWidth - a.shoulderWidth)[0];
      const advice = advise(this.#style, look.bodies, phones, this.#raiseToShoot);

      this.#draw(phones, w, h);
      this.#opts.onState({ person: look.bodies.length > 0, phones, face: main ? faceBox(main) : null, w, h, ...advice });

      // The raise signal: hands-free, nobody holding a phone, a wrist above the
      // nose for long enough to mean it.
      if (this.#style === 'free' && this.#raiseToShoot && this.#opts.onRaise) {
        const up = look.raised && !phones.some((p) => p.held);
        if (!up) {
          this.#raisedSince = 0;
        } else if (this.#armed) {
          this.#raisedSince ||= performance.now();
          if (performance.now() - this.#raisedSince >= RAISE_HOLD) {
            this.#armed = false;
            this.#opts.onRaise();
          }
        }
      }
    } catch {
      // A dropped look is not worth surfacing; the next tick retries.
    } finally {
      this.#busy = false;
    }
  }

  /**
   * Outline each phone on the viewfinder: green where it will erase, red where
   * it hides the face. The preview is drawn with `object-fit: cover`, so boxes
   * are mapped through the same crop.
   */
  #draw(phones: Phone[], w: number, h: number): void {
    const overlay = this.#opts.overlay;
    const rect = overlay.getBoundingClientRect();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const ow = Math.round(rect.width * dpr);
    const oh = Math.round(rect.height * dpr);
    if (overlay.width !== ow || overlay.height !== oh) {
      overlay.width = ow;
      overlay.height = oh;
    }
    const ctx = overlay.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, ow, oh);
    const s = Math.max(ow / w, oh / h);
    const ox = (ow - w * s) / 2;
    const oy = (oh - h * s) / 2;
    ctx.lineWidth = 3 * dpr;
    for (const p of phones) {
      const bad = p.coversFace > FACE_LIMIT;
      ctx.strokeStyle = bad ? '#ff2d55' : '#2fd38a';
      ctx.setLineDash(bad ? [] : [8 * dpr, 6 * dpr]);
      const r = 8 * dpr;
      const x = ox + p.box.x * s;
      const y = oy + p.box.y * s;
      ctx.beginPath();
      ctx.roundRect(x - 4 * dpr, y - 4 * dpr, p.box.w * s + 8 * dpr, p.box.h * s + 8 * dpr, r);
      ctx.stroke();
    }
  }
}
